import { useCallback, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { getStartingPosition, sortPlaybookRulePositionsByRank } from '@aline/common';
import { useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  SelectiveMouseSensor,
  SelectivePointerSensor,
} from 'src/components/workflow/board/utils/keyboardCoordinateGetter';
import {
  useAddPlaybookRulePosition,
  useDeletePlaybookRulePosition,
  useMovePlaybookRulePositions,
  useUpdatePlaybookRulePosition,
} from 'src/hooks';

import { getLandedRank } from './rule-drag-order';
import type { PlaybookDetailRule } from './types';

/** How far a pointer travels before a press on the ordinal becomes a drag. */
const DRAG_ACTIVATION_DISTANCE_PX = 8;

export interface PositionCommitProps {
  aiGuidance?: string | null;
  ruleText?: string;
}

/**
 * Every state transition a rule's positions can go through: edit, add, delete,
 * reorder — plus the ordering the panel numbers them by.
 *
 * Kept apart from the rendering so which position a drag lands after, and what
 * that sends, can be read and tested without a panel around it. Each of these
 * is a mutation, and the rendered order comes from the ranks in the cache.
 * The one local piece is the order a drop just landed in, held only until the
 * cache catches up; see `landedOrder`.
 */
export function useRulePositions(
  rule: PlaybookDetailRule,
  options: {
    /** Runs before a drop is sent; the review layer discards a pending move on the position here. */
    onBeforeDrop?: (positionId: UUID) => Promise<void>;
  } = {}
) {
  const { onBeforeDrop } = options;
  const { updatePlaybookRulePosition } = useUpdatePlaybookRulePosition();
  const { addPlaybookRulePosition } = useAddPlaybookRulePosition();
  const { deletePlaybookRulePosition } = useDeletePlaybookRulePosition();
  const { movePlaybookRulePositions } = useMovePlaybookRulePositions();

  /**
   * The order a drop just landed in, with the cached positions it was made
   * against. It has to apply in the same render as the drop: dnd-kit starts
   * easing the card's drag offset back to zero the moment the drag ends, so
   * an order that changes even a tick later draws the card at its new slot
   * plus the leftover offset, and it slides in from the wrong side. The
   * cache write that follows arrives that tick later, so it cannot do this
   * alone. The override is ignored as soon as the cached positions change
   * (the optimistic write, the server's reply, or a rollback), so it never
   * needs clearing to stay correct and cannot outlive the data it reordered.
   */
  const [landedOrder, setLandedOrder] = useState<{
    basePositions: PlaybookDetailRule['positions'];
    ids: readonly UUID[];
  } | null>(null);

  /**
   * Sorted here so the ordinal labelling in the panel — index 0 the Starting
   * Position, the rest Fallback 1..n — is a property of this list rather than a
   * bet on the order the server happened to send it in.
   */
  const positions = useMemo(() => {
    const byRank = sortPlaybookRulePositionsByRank(rule.positions);
    if (!landedOrder || landedOrder.basePositions !== rule.positions) return byRank;
    const indexById = new Map(landedOrder.ids.map((id, index) => [id, index]));
    return [...byRank].sort(
      (a, b) => (indexById.get(a.id) ?? byRank.length) - (indexById.get(b.id) ?? byRank.length)
    );
  }, [landedOrder, rule.positions]);

  /**
   * A rule straight out of "Add rule": no title and a single blank position.
   * The panel autofocuses the title for these so the user can start typing.
   */
  const isEmptyRule =
    !rule.title.trim() &&
    positions.length === 1 &&
    !getStartingPosition(positions)?.ruleText.trim();

  const sensors = useSensors(
    useSensor(SelectivePointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(SelectiveMouseSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    })
  );

  const commitPosition = useCallback(
    (positionId: UUID, props: PositionCommitProps): void => {
      void updatePlaybookRulePosition({ positionId, props, ruleId: rule.id });
    },
    [rule.id, updatePlaybookRulePosition]
  );

  // The position just added, so its card can take focus when it renders.
  const [addedPositionId, setAddedPositionId] = useState<UUID | null>(null);

  const addFallback = useCallback(async (): Promise<void> => {
    const { data } = await addPlaybookRulePosition({ props: { ruleText: '' }, ruleId: rule.id });
    // The mutation returns the rule with the new position appended last.
    const added = data?.addPlaybookRulePosition.positions.at(-1);
    if (added) setAddedPositionId(added.id);
  }, [addPlaybookRulePosition, rule.id]);

  const deletePosition = useCallback(
    (positionId: UUID): void => {
      void deletePlaybookRulePosition({ positionId, ruleId: rule.id });
    },
    [deletePlaybookRulePosition, rule.id]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const positionIds = positions.map(position => position.id);
      const draggedId = String(active.id);
      const oldIndex = positionIds.indexOf(draggedId);
      const newIndex = positionIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      /**
       * The whole list is reordered locally only to read off where the dragged
       * position landed; what gets sent is that one position and the one it now
       * follows. Sending the full order instead would restate positions this
       * drag never touched, overwriting a concurrent edit to them.
       */
      const landedIds = arrayMove(positionIds, oldIndex, newIndex);
      const landedIndex = landedIds.indexOf(draggedId);
      const afterPositionId = landedIndex === 0 ? null : landedIds.at(landedIndex - 1);
      if (afterPositionId === undefined) return;
      /**
       * The rule as it will read once the move lands: the dragged position
       * at a rank between its new neighbours. Without it the list keeps the
       * old order until the server replies, and the card animates back to
       * where it started before jumping to where it was dropped.
       */
      const siblingRanks = positions
        .filter(position => position.id !== draggedId)
        .map(position => position.rank);
      // Null when it cannot be shown early; the server still lands it correctly.
      const landedRank = getLandedRank(siblingRanks, landedIndex);
      const optimisticRule = landedRank
        ? {
            ...rule,
            positions: rule.positions.map(position =>
              position.id === draggedId ? { ...position, rank: landedRank } : position
            ),
          }
        : undefined;
      setLandedOrder({ basePositions: rule.positions, ids: landedIds });
      void (async () => {
        try {
          await onBeforeDrop?.(draggedId);
          await movePlaybookRulePositions({
            input: {
              afterPositionId,
              positionIds: [draggedId],
              ruleId: rule.id,
            },
            optimisticRule,
          });
        } finally {
          /**
           * Only matters when nothing changed the cache: a refused move with
           * no early write would otherwise leave the dropped order on screen.
           */
          setLandedOrder(null);
        }
      })();
    },
    [movePlaybookRulePositions, onBeforeDrop, positions, rule]
  );

  return {
    addFallback,
    addedPositionId,
    commitPosition,
    deletePosition,
    handleDragEnd,
    isEmptyRule,
    positions,
    sensors,
  };
}
